import * as Utils from '../Utils';
import {validate} from '../../privates/validations/validation';
import {runValidationsAndSyncAfterChange} from '../../privates/validationsService/validationsService';
import * as members from '../../privates/privateMembers';

/**
 * @mixin RequiredMixin
 * @summary Provides functionality for input elements that can be required to have a value.
 * @description
 *  `RequiredMixin` is a mixin that provides basic properties for elements on the page that can be required to have a value.
 * 
 *  **About Mixins**
 * 
 *  [Mixins](https://support.wix.com/en/article/velo-about-mixins) provide functionality that other elements can inherit and use. 
 *  
 *  Mixins are not elements. You cannot add mixins to a page in the Editor like other `$w` elements, and mixins are not meant to be used directly 
 *  in your code. 
 *  
 *  For example, you would not write code like this, because it is out of context: 
 * 
 *  ```javascript
 *  $w("#RequiredMixin").required = true;
 *  ```
 * 
 *  Instead, you can code the following if `myElement` is an element that 
 *  "mixes in" `RequiredMixin`. 
 * 
 *  ```javascript
 *  $w("#myElement").required = true;
 *  ```
 *  
 * @memberof $w
 */
class RequiredMixin {

    /**
     * @member required
     * @label property
     * @syntax
     * get required(): boolean
     * set required(value: boolean): void
     * @summary Sets or gets whether an input element is required to have a value.
     * @description
     *  Setting the `required` property to `true` causes the
     *  element to be invalid if it
     *  doesn't contain a value. Setting it to false allows the element to be
     *  valid even if it doesn't contain a value. The validity of the element
     *  can be checked using its [`validity`]($w.ValidatableMixin.html#validity)
     *  property.
     *
     *  Getting the value of the `required` property returns whether the element
     *  is required.
     *
     *  You can also set an input element to be required
     *  by using the element's Settings panel in the Editor.
     * @snippet [RequiredMixin-required_set.es6=Set an element to be required]
     * @snippet [RequiredMixin-required_get.es6=Get whether an element is required]
     * @type {external:Boolean}
     * @see [validity]($w.ValidatableMixin.html#validity)
     * @memberof $w.RequiredMixin
     * @attr {external:Boolean}
     */
    get required() {
        return Utils.getProp(this, 'required') || false;
    }

  /**
   * @member required
   * @memberof $w.RequiredMixin
   * @param {external:Boolean} required
   */
    set required(required) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'required',
            dataToValidate: {required},
            compName: this.id
        })) {
            return;
        }
        Utils.setProp(this, { required });
        runValidationsAndSyncAfterChange(RMI, compId);
    }
}

export default RequiredMixin;
